import React, { createContext, useContext, useEffect, useCallback, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import { toast } from '@/hooks/use-toast'
import { useAuthStore } from '@/store/authStore'
import { User } from '@/types'
import { AuthUser, PermissionType } from '@/types/auth'
import api from '@/services/api'

interface AuthContextType {
  // Legacy interface (backward compatible)
  user: User | null
  loading: boolean
  login: (email: string, password: string) => Promise<void>
  register: (email: string, password: string, firstName?: string, lastName?: string) => Promise<void>
  logout: () => void
  refreshUser: () => Promise<void>

  // Extended interface
  authUser: AuthUser | null
  isAuthenticated: boolean
  hasPermission: (permission: PermissionType) => boolean
  hasAnyPermission: (permissions: PermissionType[]) => boolean
  hasAllPermissions: (permissions: PermissionType[]) => boolean

  // OR-312 Group E: data scope
  canAccessResource: (resourceType: string, resourceId: string) => boolean
  isTenantWideIbs: boolean
  dataScopes: Record<string, string[]>

  // RBAC v1.2
  isClientAdmin: boolean
  hasAppAccess: boolean
  subscriptionActive: boolean
  subscriptionTier: string | null
  isSuperuser: boolean

  // Dev mode
  devSignIn: () => void
}

const AuthContext = createContext<AuthContextType | undefined>(undefined)

interface AuthProviderProps {
  children: React.ReactNode
}

// Convert AuthUser to legacy User type
function authUserToUser(authUser: AuthUser | null): User | null {
  if (!authUser) return null
  return {
    id: authUser.id,
    email: authUser.email,
    firstName: authUser.first_name,
    lastName: authUser.last_name,
    isActive: authUser.is_active,
    isVerified: authUser.is_verified,
    createdAt: authUser.created_at,
  }
}

export function AuthProvider({ children }: AuthProviderProps) {
  const navigate = useNavigate()

  // Zustand store
  const {
    user: authUser,
    isLoading,
    isAuthenticated,
    login: storeLogin,
    register: storeRegister,
    logout: storeLogout,
    devSignIn: storeDevSignIn,
    fetchUser,
    hasPermission,
    hasAnyPermission,
    hasAllPermissions,
    canAccessResource,
    isTenantWideIbs: storeIsTenantWideIbs,
    getDataScopes,
    isClientAdmin: storeIsClientAdmin,
    hasAppAccess: storeHasAppAccess,
    isSubscriptionActive: storeIsSubscriptionActive,
    isSuperuser: storeIsSuperuser,
    initFromStorage,
  } = useAuthStore()

  // Initialize auth from storage on mount
  useEffect(() => {
    initFromStorage()
  }, [initFromStorage])

  // Convert to legacy User type
  const user = useMemo(() => authUserToUser(authUser), [authUser])

  const login = useCallback(async (email: string, password: string) => {
    try {
      await storeLogin(email, password)
      toast({ title: 'Login successful!' })
      navigate('/')
    } catch (error: unknown) {

      // Development fallback - if backend auth fails, check for demo credentials
      const isDevelopment = import.meta.env?.MODE === 'development' || process.env.NODE_ENV === 'development'
      if (isDevelopment && email === 'demo@example.com' && password === 'demo123') {
        // Mock successful authentication for demo
        const mockToken = 'demo-token-' + Date.now()
        localStorage.setItem('access_token', mockToken)
        api.defaults.headers.common['Authorization'] = `Bearer ${mockToken}`

        // Set mock user in store directly for demo mode
        useAuthStore.setState({
          user: {
            id: 'demo-user-id',
            email: 'demo@example.com',
            first_name: 'Demo',
            last_name: 'User',
            is_active: true,
            is_verified: true,
            created_at: new Date().toISOString(),
            role: 'admin',
            permissions: [
              'ibs:view', 'ibs:create', 'ibs:update', 'ibs:delete', 'ibs:export',
              'scenario:view', 'scenario:create', 'scenario:update', 'scenario:delete',
              'risk:view', 'risk:create', 'risk:update', 'risk:delete',
              'incident:view', 'incident:create', 'incident:update', 'incident:delete',
              'compliance:view', 'compliance:create', 'compliance:update', 'compliance:delete',
              'document:view', 'document:upload', 'document:update', 'document:delete',
              'report:view', 'report:create', 'report:export',
              'master:view', 'master:create', 'master:update',
              'user:view', 'settings:view',
            ],
            is_super_admin: false,
            subscription_active: true,
            subscription_tier: 'starter',
            has_app_access: true,
            is_client_admin: true,
          },
          accessToken: mockToken,
          isAuthenticated: true,
          isLoading: false,
        })

        toast({ title: 'Login successful! (Demo Mode)' })
        navigate('/')
        return
      }

      const errorMessage = (error as { response?: { data?: { detail?: string } } })?.response?.data?.detail || 'Login failed'
      toast({ title: errorMessage, variant: 'destructive' })
      throw error
    }
  }, [storeLogin, navigate])

  const register = useCallback(async (
    email: string,
    password: string,
    firstName?: string,
    lastName?: string
  ) => {
    try {
      await storeRegister(email, password, firstName, lastName)
      toast({ title: 'Registration successful!' })
      navigate('/')
    } catch (error: unknown) {
      const errorMessage = (error as { response?: { data?: { detail?: string } } })?.response?.data?.detail || 'Registration failed'
      toast({ title: errorMessage, variant: 'destructive' })
      throw error
    }
  }, [storeRegister, navigate])

  const logout = useCallback(() => {
    storeLogout()
    toast({ title: 'Logged out successfully' })
    navigate('/login')
  }, [storeLogout, navigate])

  const refreshUser = useCallback(async () => {
    try {
      await fetchUser()
    } catch (error: unknown) {
      const status = (error as { response?: { status?: number } })?.response?.status
      if (status === 401) {
        logout()
      }
    }
  }, [fetchUser, logout])

  const devSignIn = useCallback(() => {
    storeDevSignIn()
    toast({ title: 'Signed in as Dev User' })
    navigate('/')
  }, [storeDevSignIn, navigate])

  // Derive RBAC v1.2 values
  const isClientAdmin = storeIsClientAdmin()
  const hasAppAccess = storeHasAppAccess()
  const subscriptionActive = storeIsSubscriptionActive()
  const subscriptionTier = authUser?.subscription_tier ?? null
  const isSuperuser = storeIsSuperuser()
  // OR-312 Group E: derive scope values (recomputed when authUser changes).
  const isTenantWideIbs = storeIsTenantWideIbs()
  const dataScopes = getDataScopes()

  const value = useMemo(() => ({
    // Legacy interface
    user,
    loading: isLoading,
    login,
    register,
    logout,
    refreshUser,

    // Extended interface
    authUser,
    isAuthenticated,
    hasPermission,
    hasAnyPermission,
    hasAllPermissions,

    // OR-312 Group E: data scope
    canAccessResource,
    isTenantWideIbs,
    dataScopes,

    // RBAC v1.2
    isClientAdmin,
    hasAppAccess,
    subscriptionActive,
    subscriptionTier,
    isSuperuser,

    // Dev mode
    devSignIn,
  }), [
    user,
    isLoading,
    login,
    register,
    logout,
    refreshUser,
    authUser,
    isAuthenticated,
    hasPermission,
    hasAnyPermission,
    hasAllPermissions,
    canAccessResource,
    isTenantWideIbs,
    dataScopes,
    isClientAdmin,
    hasAppAccess,
    subscriptionActive,
    subscriptionTier,
    isSuperuser,
    devSignIn,
  ])

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  )
}

// eslint-disable-next-line react-refresh/only-export-components -- Hook intentionally exported with provider
export function useAuth() {
  const context = useContext(AuthContext)
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}

// eslint-disable-next-line react-refresh/only-export-components -- Hook intentionally exported with provider
export function usePermission(permission: PermissionType): boolean {
  const { hasPermission } = useAuth()
  return hasPermission(permission)
}

// eslint-disable-next-line react-refresh/only-export-components -- Hook intentionally exported with provider
export function usePermissions(permissions: PermissionType[], requireAll = false): boolean {
  const { hasAnyPermission, hasAllPermissions } = useAuth()
  return requireAll ? hasAllPermissions(permissions) : hasAnyPermission(permissions)
}
